/**
 * Component: TopAccessedContent
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R09
 *
 * Table component displaying top accessed content ranked by specialty or topic.
 * ADMIN-V1-R09: System shall provide a usage dashboard displaying top accessed
 *               content by specialty or topic.
 * Accessibility: WCAG 2.1 AA compliant with proper ARIA labels
 */

import React, { useMemo, useState } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown, Filter } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * Data point for top accessed content
 */
export interface TopContentItem {
  /** Unique identifier for the content */
  id: string;
  /** Content title or name */
  title: string;
  /** Specialty or topic category */
  category: string;
  /** Number of accesses */
  accessCount: number;
  /** Content type (guideline, publication, etc.) */
  contentType: 'guideline' | 'publication';
}

export interface TopAccessedContentProps {
  /** Array of content items to display */
  data: TopContentItem[];
  /** Maximum number of items to display (default: 10) */
  limit?: number;
  /** Loading state */
  isLoading?: boolean;
  /** Accessible description for screen readers */
  description?: string;
}

type SortField = 'title' | 'category' | 'accessCount';
type SortDirection = 'asc' | 'desc';

/**
 * Loading skeleton for the table
 */
function TableSkeleton(): React.ReactElement {
  return (
    <div
      className="animate-pulse space-y-3"
      aria-label="Loading content rankings"
      role="progressbar"
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center space-x-4">
          <div className="h-4 w-8 rounded bg-muted" />
          <div className="h-4 flex-1 rounded bg-muted" />
          <div className="h-4 w-24 rounded bg-muted" />
          <div className="h-4 w-16 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

/**
 * Empty state when no data is available
 */
function EmptyState(): React.ReactElement {
  return (
    <div
      className="flex h-full min-h-[200px] items-center justify-center rounded-md border border-dashed"
      role="status"
    >
      <p className="text-sm text-muted-foreground">
        No content access data available for the selected period
      </p>
    </div>
  );
}

/**
 * Sort button component for table headers
 */
interface SortButtonProps {
  field: SortField;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
}

function SortButton({
  field,
  currentField,
  direction,
  onSort,
  children,
}: SortButtonProps): React.ReactElement {
  const isActive = currentField === field;

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => onSort(field)}
      aria-label={`Sort by ${field}${isActive ? `, currently ${direction}ending` : ''}`}
    >
      {children}
      {isActive ? (
        direction === 'asc' ? (
          <ArrowUp className="ml-2 h-4 w-4" aria-hidden="true" />
        ) : (
          <ArrowDown className="ml-2 h-4 w-4" aria-hidden="true" />
        )
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
      )}
    </Button>
  );
}

/**
 * Table displaying top accessed content ranked by specialty or topic.
 *
 * ADMIN-V1-R09: Provides ranking of content by category with sortable columns.
 */
export function TopAccessedContent({
  data,
  limit = 10,
  isLoading = false,
  description = 'Table showing top accessed content by specialty or topic',
}: TopAccessedContentProps): React.ReactElement {
  const [sortField, setSortField] = useState<SortField>('accessCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Extract unique categories for the filter dropdown
  const categories = useMemo(() => {
    const uniqueCategories = new Set(data.map((item) => item.category));
    return Array.from(uniqueCategories).sort();
  }, [data]);

  // Filter, sort, and limit data
  const sortedData = useMemo(() => {
    const filtered = categoryFilter === 'all'
      ? data
      : data.filter((item) => item.category === categoryFilter);

    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        case 'accessCount':
          comparison = a.accessCount - b.accessCount;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted.slice(0, limit);
  }, [data, sortField, sortDirection, limit, categoryFilter]);

  // Handle sort toggle
  const handleSort = (field: SortField): void => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (data.length === 0) {
    return <EmptyState />;
  }

  return (
    <div
      role="region"
      aria-label={description}
      className="w-full"
    >
      {/* Screen reader description */}
      <span className="sr-only">
        {description}. Showing {sortedData.length} of {data.length} items,
        sorted by {sortField} in {sortDirection}ending order.
        {categoryFilter !== 'all' && ` Filtered by ${categoryFilter}.`}
      </span>

      {/* Specialty/Topic Filter (ADMIN-V1-R09) */}
      {categories.length > 1 && (
        <div className="mb-4 flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <label htmlFor="category-filter" className="text-sm font-medium">
            Specialty/Topic:
          </label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger id="category-filter" className="w-48" aria-label="Filter by specialty or topic">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>
              <SortButton
                field="title"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              >
                Content
              </SortButton>
            </TableHead>
            <TableHead>
              <SortButton
                field="category"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              >
                Specialty/Topic
              </SortButton>
            </TableHead>
            <TableHead className="text-right">
              <SortButton
                field="accessCount"
                currentField={sortField}
                direction={sortDirection}
                onSort={handleSort}
              >
                Accesses
              </SortButton>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((item, index) => (
            <TableRow key={item.id}>
              <TableCell className="font-medium text-muted-foreground">
                {index + 1}
              </TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="font-medium">{item.title}</span>
                  <span className="text-xs text-muted-foreground capitalize">
                    {item.contentType}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs font-medium">
                  {item.category}
                </span>
              </TableCell>
              <TableCell className="text-right font-medium">
                {item.accessCount.toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {data.length > limit && (
        <p className="mt-2 text-xs text-muted-foreground">
          Showing top {limit} of {data.length} items
        </p>
      )}
    </div>
  );
}

export default TopAccessedContent;
